import { useEffect, useMemo } from "react";

import Valkyrie, { viCheck, viCircleExclamation, viSpinnerThird } from "@sippy-platform/valkyrie";

import { Box, Button, Tooltip, Typography } from "@mui/material";
import { useStore } from "@tanstack/react-form";
import { useBatchedCallback } from "@tanstack/react-pacer";

import { useFormContext } from "./useAppForm";

export function SubmitButton({ query, delay = 1000 }) {
  const form = useFormContext();

  const isValid = useStore(form.store, (state) => state.isValid);
  const isDirty = useStore(form.store, (state) => state.isDirty);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const isSubmitSuccessful = useStore(form.store, (state) => state.isSubmitSuccessful);
  const values = useStore(form.store, (state) => state.values);

  const status = useMemo(() => {
    if (query.isError && !isSubmitting) {
      return {
        message: "An unknown error occurred.",
        color: "error",
        label: "Failed",
        icon: { icon: viCircleExclamation },
      };
    } else {
      if (isDirty && !isSubmitting) {
        if (isValid) {
          return {
            message: "We'll save your changes shortly.",
            color: "primary",
            label: "Unsaved",
            icon: { icon: viCheck },
          };
        } else {
          return {
            message: "Awaiting your changes...",
            color: "primary",
            label: "Changes required",
            icon: { icon: viCircleExclamation },
          };
        }
      } else if (isSubmitting) {
        return {
          message: "Saving your changes...",
          color: "primary",
          label: "Saving",
          icon: { icon: viSpinnerThird, spin: true },
        };
      }

      if (isSubmitSuccessful) {
        return { message: "Everything is up-to-date.", color: "success", label: "Saved", icon: { icon: viCheck } };
      }

      return { message: "Everything is up-to-date.", color: "success", label: "Up-to-date", icon: { icon: viCheck } };
    }
  }, [isDirty, isSubmitSuccessful, isSubmitting, isValid, query.isError]);

  // Handle saving changes
  const batchedSave = useBatchedCallback(async () => form.handleSubmit(), { wait: delay, maxSize: 100 });

  // Call the batch save function when valid and dirty
  useEffect(() => {
    if (isValid && isDirty) batchedSave();
  }, [values, batchedSave, isValid, isDirty]);

  return (
    <form.Subscribe>
      {() => (
        <Tooltip
          title={
            !isValid ? (
              <>
                <Typography>Solve these issues to save your changes:</Typography>
                <Box component="ul" sx={{ pl: 2.5, mb: 0, mt: 1 }}>
                  {Object.entries(form.state.errorMap.onChange).map(([field, errors]) =>
                    errors.map((error, errorKey) => (
                      <Typography key={`${field}-${errorKey}`} className="list list-disc">
                        <li>{error.message}</li>
                      </Typography>
                    )),
                  )}
                </Box>
              </>
            ) : (
              <Typography>{status.message}</Typography>
            )
          }
        >
          <Button type="submit" color={status.color} onClick={() => form.handleSubmit()}>
            <Valkyrie {...status.icon} className="@min-lg/detail:me-2" />
            <span className="@max-lg/detail:hidden">{status.label}</span>
          </Button>
        </Tooltip>
      )}
    </form.Subscribe>
  );
}
